import React, { useEffect, useState } from 'react';
import { bool, object } from 'prop-types';
import { connect } from 'react-redux';

import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import SeoIndexAlphabetNav from '../../components/SeoDiscovery/SeoIndexAlphabetNav';
import { propTypes } from '../../util/types';
import css from '../../components/SeoDiscovery/SeoDiscovery.module.css';
import { fetchSeoPerformersIndex } from '../../util/seoDiscoveryFetch';
import { INDEX_LETTERS, performersLetterPath } from '../../util/seoDiscoveryIndexLetters';

export const SeoDiscoveryPerformersIndexPageComponent = props => {
  const { data, fetchInProgress, fetchError } = props;
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchInProgress, setSearchInProgress] = useState(false);
  const letterCounts = data?.letterCounts || {};
  const normalizedQuery = searchQuery.trim();
  const hasSearch = normalizedQuery.length > 0;
  const performers = hasSearch ? searchResults?.performers || [] : [];
  const total = hasSearch ? searchResults?.total || 0 : 0;

  useEffect(() => {
    if (!hasSearch) {
      setSearchResults(null);
      return undefined;
    }
    setSearchInProgress(true);
    const handle = window.setTimeout(() => {
      fetchSeoPerformersIndex({
        q: normalizedQuery,
        limit: '500',
      })
        .then(payload => setSearchResults(payload))
        .catch(() => setSearchResults({ performers: [], total: 0 }))
        .finally(() => setSearchInProgress(false));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [hasSearch, normalizedQuery]);

  return (
    <Page
      title="Performers | Event Discovery | PromoTix"
      description="Browse performers on PromoTix. Find upcoming events, tickets, and tour dates by artist or show."
      schema={{
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'PromoTix Performers',
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <h1 className={css.h1}>Performers</h1>
          <p>Discover upcoming events and tickets by artist, show, or team.</p>
          {fetchError ? <p>Unable to load performers.</p> : null}
          {fetchInProgress && !Object.keys(letterCounts).length ? <p>Loading…</p> : null}
          <div className={css.venueSearch}>
            <label className={css.venueSearchLabel} htmlFor="performer-index-search">
              Find a specific performer
            </label>
            <div className={css.venueSearchControl}>
              <input
                id="performer-index-search"
                className={css.venueSearchInput}
                type="search"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by performer name"
                autoComplete="off"
              />
              {hasSearch ? (
                <button
                  className={css.venueSearchClear}
                  type="button"
                  onClick={() => setSearchQuery('')}
                >
                  Clear
                </button>
              ) : null}
            </div>
            {hasSearch ? (
              <p className={css.venueSearchHelp} aria-live="polite">
                {searchInProgress
                  ? 'Searching…'
                  : `${total} performer${total === 1 ? '' : 's'} found`}
              </p>
            ) : null}
          </div>
          {!hasSearch ? (
            <>
              <SeoIndexAlphabetNav
                letterCounts={letterCounts}
                getLetterHref={performersLetterPath}
              />
              <ul className={css.indexHubLetters}>
                {INDEX_LETTERS.filter(letter => (letterCounts[letter] || 0) > 0).map(letter => (
                  <li key={letter}>
                    <a href={performersLetterPath(letter)}>
                      {letter === '#' ? '0–9' : letter} — {letterCounts[letter]} performer
                      {letterCounts[letter] === 1 ? '' : 's'}
                    </a>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {hasSearch && performers.length > 0 ? (
            <section className={css.letterSection}>
              <h2 className={css.letterHeading}>Search results</h2>
              <ul className={css.indexList}>
                {performers.map(p => (
                  <li key={p.slug || p.performerId}>
                    <a href={p.path}>{p.performerName}</a>
                    <span className={css.eventMeta}>
                      {' '}
                      — {p.eventCount || 0} event{(p.eventCount || 0) === 1 ? '' : 's'}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {hasSearch && !searchInProgress && total === 0 ? (
            <p className={css.venueSearchNoResults}>
              No performers found for "{normalizedQuery}". Try another name.
            </p>
          ) : null}
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

SeoDiscoveryPerformersIndexPageComponent.propTypes = {
  data: object,
  fetchInProgress: bool,
  fetchError: propTypes.error,
};

const mapStateToProps = state => state.SeoDiscoveryPerformersIndexPage || {};

export default connect(mapStateToProps)(SeoDiscoveryPerformersIndexPageComponent);
